import React, { useState } from 'react';
import { Box, Link, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import type { ChangeSetItem } from '../../../api/ontologyChangeSetApi';
import ValidationMessages from '../ValidationMessages';
import { formatValue } from '../ontologyFormat';
import {
    CHANGE_TYPE_COLOR, Choice, EDITABLE_FIELDS, canAccept, canEdit, differsFromProposed, editText,
    itemLabel, parseEditValue, validationErrors, validationWarnings,
} from './changeSetReview';
import { FIELD_SX, MONO_SX, Pill } from './changeSetUi';
import { dsVar, ink } from '../../../theme/tokens';

export interface ChangeSetItemCardProps {
    item: ChangeSetItem;
    /** The document's viewer URL, for "p. N" evidence links. */
    sourceLink?: string;
    /** Read-only: a decided change set, or one this user cannot decide. */
    readOnly?: boolean;
    choice?: Choice;
    onChoice?: (choice: Choice | undefined) => void;
    edits?: Record<string, unknown>;
    onEdits?: (edits: Record<string, unknown>) => void;
    note?: string;
    onNote?: (note: string) => void;
    /** Labels of the rejected items this one depends on, when it will be rejected with them. */
    cascadeCauses?: string[];
    /** Formatted "Takes effect on …" date, for a written item. */
    takesEffectOn?: string | null;
}

const show = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const KeyValue: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ display: 'flex', gap: 1.5, fontSize: '0.8125rem', lineHeight: 1.6 }}>
        <Box sx={{ color: dsVar('fg-muted'), minWidth: 96, flexShrink: 0 }}>{label}</Box>
        <Box sx={{ color: dsVar('fg-soft'), minWidth: 0, wordBreak: 'break-word' }}>{children}</Box>
    </Box>
);

/** The fields of `proposed` a reviewer needs to judge an addition (spec §4.4). */
const ProposedSummary: React.FC<{ item: ChangeSetItem }> = ({ item }) => {
    const p = item.proposed ?? {};
    switch (item.collection) {
        case 'parameter':
            return (
                <>
                    <KeyValue label="Threshold">
                        <Box component="span" sx={{ color: dsVar('accent-fg'), fontWeight: 600 }}>
                            {[p.operator, formatValue(p.value, p.unit)].filter(Boolean).join(' ')}
                        </Box>
                    </KeyValue>
                    {(item.conceptCode || p.conceptCode) && (
                        <KeyValue label="Applies to"><Box component="span" sx={MONO_SX}>{item.conceptCode || p.conceptCode}</Box></KeyValue>
                    )}
                    {p.scope && Object.keys(p.scope).length > 0 && <KeyValue label="Scope">{show(p.scope)}</KeyValue>}
                </>
            );
        case 'concept':
            return (
                <>
                    {p.definition && <KeyValue label="Definition">{p.definition}</KeyValue>}
                    {p.conceptType && <KeyValue label="Type">{p.conceptType}</KeyValue>}
                </>
            );
        case 'policy': {
            const clauses: Array<Record<string, any>> = Array.isArray(p.clauses) ? p.clauses : [];
            return (
                <>
                    {p.narrative && <KeyValue label="Narrative">{p.narrative}</KeyValue>}
                    {clauses.length > 0 && (
                        <KeyValue label={`Clauses (${clauses.length})`}>
                            {clauses.slice(0, 5).map((clause, index) => (
                                <Box key={clause.clauseId ?? index} sx={{ mb: 0.5 }}>
                                    <Box component="span" sx={{ ...MONO_SX, color: dsVar('fg-2'), mr: 0.75 }}>{clause.clauseId}</Box>
                                    {clause.heading ? <strong>{clause.heading}. </strong> : null}
                                    {clause.text}
                                </Box>
                            ))}
                            {clauses.length > 5 && <Box sx={{ color: dsVar('fg-muted') }}>…and {clauses.length - 5} more</Box>}
                        </KeyValue>
                    )}
                </>
            );
        }
        case 'source_document':
            return (
                <>
                    {p.issuer && <KeyValue label="Issuer">{p.issuer}</KeyValue>}
                    {p.versionLabel && <KeyValue label="Version">{p.versionLabel}</KeyValue>}
                    {p.effectiveDate && <KeyValue label="Effective">{p.effectiveDate}</KeyValue>}
                </>
            );
        default:
            return null;
    }
};

const DiffList: React.FC<{ item: ChangeSetItem }> = ({ item }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {(item.diff ?? []).map((entry, index) => (
            <Box key={`${entry.field}-${index}`} sx={{ fontSize: '0.8125rem', lineHeight: 1.6 }}>
                <Box component="span" sx={{ ...MONO_SX, color: dsVar('fg-2'), mr: 1 }}>{entry.field}</Box>
                {entry.change ? (
                    <Box component="span" sx={{ color: entry.change === 'added' ? ink('#4ADE80') : ink('#F87171') }}>{entry.change}</Box>
                ) : entry.added || entry.removed ? (
                    <>
                        {(entry.added ?? []).length > 0 && <Box component="span" sx={{ color: ink('#4ADE80'), mr: 1 }}>+ {(entry.added ?? []).join(', ')}</Box>}
                        {(entry.removed ?? []).length > 0 && <Box component="span" sx={{ color: ink('#F87171') }}>− {(entry.removed ?? []).join(', ')}</Box>}
                    </>
                ) : (
                    <>
                        <Box component="span" sx={{ color: dsVar('fg-2'), textDecoration: 'line-through' }}>{show(entry.before)}</Box>
                        <Box component="span" sx={{ color: dsVar('fg-muted'), mx: 0.75 }}>→</Box>
                        <Box component="span" sx={{ color: dsVar('accent-fg') }}>{show(entry.after)}</Box>
                    </>
                )}
            </Box>
        ))}
    </Box>
);

/** Inline edit fields for the collection's editable fields (spec §5.2). */
const EditFields: React.FC<{
    item: ChangeSetItem;
    edits: Record<string, unknown>;
    onEdits: (edits: Record<string, unknown>) => void;
}> = ({ item, edits, onEdits }) => {
    const fields = EDITABLE_FIELDS[item.collection] ?? [];
    const proposed = item.proposed ?? {};
    const [raw, setRaw] = useState<Record<string, string>>(() => Object.fromEntries(
        fields.map((field) => [field, editText(field in edits ? edits[field] : proposed[field])]),
    ));
    const [errors, setErrors] = useState<Record<string, string>>({});

    const change = (field: string, text: string) => {
        setRaw((current) => ({ ...current, [field]: text }));
        const parsed = parseEditValue(proposed[field], text);
        const next = { ...edits };
        if (parsed.error) {
            setErrors((current) => ({ ...current, [field]: parsed.error as string }));
            delete next[field];
        } else {
            setErrors(({ [field]: _removed, ...rest }) => rest);
            if (differsFromProposed(item, field, parsed.value)) next[field] = parsed.value;
            else delete next[field];
        }
        onEdits(next);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1.5 }}>
            {fields.map((field) => {
                const original = proposed[field];
                const multiline = typeof original === 'object' || (typeof original === 'string' && original.length > 60);
                return (
                    <TextField
                        key={field}
                        label={field}
                        size="small"
                        fullWidth
                        multiline={multiline}
                        minRows={multiline ? 2 : undefined}
                        maxRows={10}
                        value={raw[field] ?? ''}
                        onChange={(e) => change(field, e.target.value)}
                        error={Boolean(errors[field])}
                        helperText={errors[field] || (typeof original === 'object' && original !== null ? 'JSON. Replaces the proposed value whole.' : undefined)}
                        sx={FIELD_SX}
                    />
                );
            })}
        </Box>
    );
};

const CHOICE_SX = {
    textTransform: 'none',
    fontSize: '0.75rem',
    px: 1.25,
    py: 0.25,
    color: dsVar('fg-2'),
    borderColor: dsVar('line'),
    '&.Mui-selected[value="accept"]': { color: dsVar('on-accent'), backgroundColor: '#4ADE80' },
    '&.Mui-selected[value="reject"]': { color: dsVar('on-accent'), backgroundColor: '#F87171' },
    '&.Mui-selected[value="edit"]': { color: dsVar('on-accent'), backgroundColor: '#60A5FA' },
} as const;

const DECISION_COLOR: Record<string, string> = {
    accepted: '#4ADE80', edited: '#60A5FA', rejected: '#F87171', pending: '#94A3B8',
};

/**
 * One proposed change: what it is, the document's evidence for it, anything that should
 * give the reviewer pause, and the Accept / Reject / Edit control.
 */
const ChangeSetItemCard: React.FC<ChangeSetItemCardProps> = ({
    item, sourceLink, readOnly, choice, onChoice, edits = {}, onEdits, note = '', onNote, cascadeCauses, takesEffectOn,
}) => {
    const errors = validationErrors(item);
    const warnings = validationWarnings(item);
    const acceptable = canAccept(item);
    const editable = canEdit(item);
    const cascaded = Boolean(cascadeCauses?.length);
    const decided = readOnly && item.decision && item.decision !== 'pending';

    return (
        <Box
            data-testid={`item-${item.itemId}`}
            sx={{
                p: 1.75, borderRadius: '8px', border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page'),
                opacity: cascaded ? 0.7 : 1,
            }}
        >
            {/* Header: change type, label, code */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Pill color={CHANGE_TYPE_COLOR[item.changeType] ?? '#94A3B8'}>{item.changeType}</Pill>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: dsVar('fg'), lineHeight: 1.4 }}>
                        {itemLabel(item)}
                    </Typography>
                    <Typography sx={{ ...MONO_SX, color: dsVar('fg-muted') }}>{item.code}</Typography>
                </Box>
                {decided && (
                    <Pill color={DECISION_COLOR[item.decision as string] ?? '#94A3B8'}>{item.decision}</Pill>
                )}
            </Box>

            {/* Flags, beside the item rather than in a detail panel (spec §4.4). */}
            {(item.confidence === 'low' || item.newConcept || item.overridesTier || Boolean(item.conflictingValues)) && (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                    {item.confidence === 'low' && (
                        <Tooltip title="The quoted text could not be found on the cited page.">
                            <span><Pill color="#FBBF24">Low confidence</Pill></span>
                        </Tooltip>
                    )}
                    {item.newConcept && (
                        <Tooltip title="Matches nothing in the library: accepting it adds vocabulary.">
                            <span><Pill color="#C4B5FD">New concept</Pill></span>
                        </Tooltip>
                    )}
                    {item.overridesTier && (
                        <Tooltip title="Changes a platform element. Your organisation gets its own copy; the platform's is not touched.">
                            <span><Pill color="#60A5FA">Overrides platform</Pill></span>
                        </Tooltip>
                    )}
                    {Boolean(item.conflictingValues) && (
                        <Tooltip title={`The document states more than one value: ${show(item.conflictingValues)}`}>
                            <span><Pill color="#F87171">Conflicting values</Pill></span>
                        </Tooltip>
                    )}
                </Box>
            )}

            {Boolean(item.conflictingValues) && (
                <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5'), mb: 1 }}>
                    The document states more than one value for this: {show(item.conflictingValues)}
                </Typography>
            )}

            {/* What changes */}
            <Box sx={{ mb: 1 }}>
                {item.changeType === 'modify' && <DiffList item={item} />}
                {item.changeType === 'add' && <ProposedSummary item={item} />}
                {item.changeType === 'retire' && (
                    <>
                        {item.incumbent && (
                            <KeyValue label="In force">
                                <Box component="span" sx={MONO_SX}>{item.incumbent.code ?? item.code}</Box>
                                {item.incumbent.scopeLevel && ` · ${item.incumbent.scopeLevel}`}
                                {item.incumbent.effectiveFrom && ` · since ${item.incumbent.effectiveFrom}`}
                            </KeyValue>
                        )}
                        {item.reason && <KeyValue label="Why retire">{item.reason}</KeyValue>}
                    </>
                )}
            </Box>

            {/* Evidence */}
            {(item.evidence ?? []).length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
                    {(item.evidence ?? []).map((evidence, index) => (
                        <Box key={index} sx={{ borderLeft: `2px solid ${dsVar('line')}`, pl: 1.25, fontSize: '0.8125rem', color: dsVar('fg-2'), fontStyle: 'italic', lineHeight: 1.6 }}>
                            “{evidence.quote}”
                            {evidence.pageNumber !== undefined && (
                                sourceLink ? (
                                    <Link
                                        href={`${sourceLink}#page=${evidence.pageNumber}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ ml: 0.75, color: dsVar('accent-fg'), fontStyle: 'normal', fontSize: '0.75rem' }}
                                    >
                                        p. {evidence.pageNumber}
                                    </Link>
                                ) : (
                                    <Box component="span" sx={{ ml: 0.75, fontStyle: 'normal', fontSize: '0.75rem' }}>p. {evidence.pageNumber}</Box>
                                )
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            <ValidationMessages errors={errors} warnings={warnings} />

            {cascaded && (
                <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5'), mb: 1 }}>
                    Will also be rejected: it depends on {cascadeCauses?.join(', ')}.
                </Typography>
            )}

            {/* After a decision: what was written */}
            {decided && (
                <Box sx={{ mt: 0.5 }}>
                    {item.reviewNote && <KeyValue label="Note">{item.reviewNote}</KeyValue>}
                    {item.edits && Object.keys(item.edits).length > 0 && <KeyValue label="Edited">{show(item.edits)}</KeyValue>}
                    {item.writeReport && (
                        <KeyValue label="Written">
                            {item.writeReport.written ? (
                                <Box component="span" sx={{ color: ink('#4ADE80') }}>Yes{takesEffectOn ? ` · takes effect on ${takesEffectOn}` : ''}</Box>
                            ) : (
                                <Box component="span" sx={{ color: ink('#F87171') }}>
                                    No{item.writeReport.action === 'skipped' && item.writeReport.reason ? ` (skipped: ${item.writeReport.reason})` : ''}
                                    {(item.writeReport.problems ?? []).length > 0 && ` — ${(item.writeReport.problems ?? []).map(show).join('; ')}`}
                                </Box>
                            )}
                        </KeyValue>
                    )}
                </Box>
            )}

            {/* The decision control */}
            {!readOnly && onChoice && (
                <Box sx={{ mt: 1.25 }}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={choice ?? null}
                        onChange={(_e, value) => onChoice(value ?? undefined)}
                        aria-label={`Decision for ${item.code}`}
                    >
                        {/* Disabled rather than hidden: the validation error above says why. */}
                        <ToggleButton value="accept" disabled={!acceptable} sx={CHOICE_SX}>
                            {item.changeType === 'retire' ? 'Accept (retire)' : 'Accept'}
                        </ToggleButton>
                        <ToggleButton value="reject" sx={CHOICE_SX}>
                            {item.changeType === 'retire' ? 'Reject (keep)' : 'Reject'}
                        </ToggleButton>
                        {editable && <ToggleButton value="edit" sx={CHOICE_SX}>Edit</ToggleButton>}
                    </ToggleButtonGroup>

                    {choice === 'edit' && onEdits && <EditFields item={item} edits={edits} onEdits={onEdits} />}

                    {(choice === 'reject' || choice === 'edit') && onNote && (
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Note (optional)"
                            value={note}
                            onChange={(e) => onNote(e.target.value)}
                            sx={{ ...FIELD_SX, mt: 1.25 }}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ChangeSetItemCard;
